import { useLocation, useOutletContext, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { usePrice } from '../../components/Context/Context'
import { nextPage } from 'components/RCPro/helpers/nav'
import ProgressBarAnimated from 'components/common/ProgressBarAnimate/ProgressBarAnimated'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import api from 'helpers/api'
import style from './Loading.module.scss'

const RCProLoading = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [ searchParams ] = useSearchParams()
  const reason = searchParams.get('reason')
  const { state, pathname } = useLocation()
  const { loading } = state.texts
  const domains = state.texts.activity?.domains
  const { nextPageTransition } = useOutletContext()
  const [ achieved, setAchieved ] = useState(0)
  const [ failed, setFailed ] = useState(false)
  const { price: { price, tarificateur, LOI }, getPrice } = usePrice()
  const [ adhesionId, setAdhesionId ] = useState(reason !== 'updating' && state.adhesionId)
  const [ APICall, setAPICall ] = useState(false)
  const hasFetched = useRef(false)


  useEffect(() => {
    const {
      contractType,
      priceType,
      rateType,
      firstname,
      lastname,
      organizationName,
      companyImmatriculation,
      headquarterAddress,
      headquarterCity,
      headquarterZipcode,
      sector,
      codeProduct,
      activity,
      textActivityDocument,
      turnover,
      email,
      phone: { number, countryCode }
    } = state
  
    const data = {
      firstname,
      lastname,
      organizationName,
      companyImmatriculation,
      headquarterAddress,
      headquarterCity,
      headquarterZipcode,
      sector,
      price,
      calculatedPrice: price,
      tarificateur,
      codeProduct,
      activity,
      LOI,
      textActivityDocument,
      turnover,
      email,
      phone: number,
      countryCode
    }

    const headers = {
      Authorization: `Bearer ${state.token}`
    }

    const oups = (errMessage) => {
      navigate('../oups?reason=401', { state: {
        ...state, errMessage
      }})
    }

    // Checking tarificateur
    if (!hasFetched.current){
      hasFetched.current = true
      getPrice({ codeProduct, turnover, activity, token: state.token, check: true })
    }

    const createAdhesion = async () => {
      try{
        const res = await api.post({
          url: '/adhesions/client',
          data: {
            ...data,
            contractType,
            priceType,
            rateType,
            areTermsAccepted: true,
            paymentPlan: 'year',
            birthdate: '01/01/2000',
            transactionHorodate: new Date().toISOString()
          },
          headers
        })

        setAPICall(false)
        if (res?.response?.id) {
          setAdhesionId(res.response.id)
        } else {
          oups('POST /adhesions/client did not failed but returned no response id')
        }
      } catch (error) {
        setFailed(true)
        switch (error.message){
          case "You're already subscribed to this contract":
            setTimeout( () => {
              navigate('../oups?reason=registered-email', { state: { ...state, ...data } })
            }, 250)
            nextPageTransition()
            break;
          default: oups('POST /adhesions/client returned '+error.message)
            break;
        }
      }
    }

    const updateAdhesion = async () => {
      try{
        data.updatedAt = Date.now()
        const res = await api.patch({
          url: '/RCPro/update-adhesion/'+state.adhesionId,
          data,
          headers
        })

        setAPICall(false)
        if (res?.message) {
          setAdhesionId(state.adhesionId)
        } else {
          oups(`PATCH /RCPro/update-adhesion/${state.adhesionId} did not failed but returned no message`)
          setFailed(true)
        }
      } catch (error) {
        setFailed(true)
        switch (error.message){
          case "This email already subscribed to this contract":
            setTimeout( () => {
              navigate('../oups?reason=registered-email', { state })
            }, 250)
            nextPageTransition()
            break;
          default: oups(`PATCH /RCPro/update-adhesion/${state.adhesionId} returned ${error.message}`)
            break;
        }
      }
    }

    const delay = achieved === 0
      ? 500
      : achieved === 4
        ? 1000
        : 1500

    const interval = setInterval( () => {
      // Error happened
      if (failed){
        clearInterval(interval)
      }

      // Progress bar not completed
      else if (achieved < 4) {
        // Which endpoint to call
        if (price && !adhesionId && !APICall) {
          setAPICall(true)
          if (reason === 'updating'){
            updateAdhesion()
          } else {
            createAdhesion()
          }
        }

        // Are there price and adhesion id
        if (achieved < 3) {
          setAchieved(achieved+1)
        } else if (price && adhesionId){
          setAchieved(4)
        }
      }

      // All done
      else if (achieved === 4) {
        clearInterval(interval)
        nextPage({ pathname, state, navigate, data: { ...state, price, LOI, adhesionId } })
        nextPageTransition()
      }
    }, delay)
    return () => clearInterval(interval);
  }, [achieved, adhesionId, domains, navigate, nextPageTransition, pathname, price, tarificateur, state, failed, reason, LOI, APICall, getPrice])

  return (
    <div className={style.content}>
      <h1>{loading?.title[achieved]} ⏳</h1>
      {failed 
        ? <ResponseMessage className={style.response} label={t('common:form.genericError')} />
        : <p>{loading?.resume}</p>
      }
      <ProgressBarAnimated
        barStyle={style.progressBar}
        achieved={achieved * 25}
        failed={failed}
      />
    </div>
  )
}

export default RCProLoading